import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { UserPlus, Pencil, Loader2, ShieldCheck } from "lucide-react";
import { fmtDateTime, PREFERENCE_OPTIONS } from "./SendDialogs";

const EMPTY_FORM = {
  full_name: "",
  email: "",
  job_title: "",
  department: "",
  phone: "",
  notes: "",
  source: "externo",
  status: "ativo",
  preferences: { boletins: true, alertas: true, pga: true },
  unit_ids: [],
};

/**
 * Aba Destinatários.
 *
 * O cadastro é por cliente e unidades autorizadas. Contatos externos
 * recebem as comunicações sem que nenhuma conta de acesso seja criada.
 */
export default function RecipientsTab({ clientId, onChanged }) {
  const [rows, setRows] = useState([]);
  const [unitsCatalog, setUnitsCatalog] = useState([]);
  const [counts, setCounts] = useState(null);
  const [status, setStatus] = useState("todos");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/envintel/recipients", {
        params: {
          client_id: clientId,
          status: status === "todos" ? undefined : status,
          search: search || undefined,
        },
      });
      setRows(data.recipients || []);
      setCounts(data.counts || null);
      setUnitsCatalog(data.units || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [clientId, status, search]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({
      ...EMPTY_FORM,
      unit_ids: unitsCatalog[0] ? [unitsCatalog[0].id] : [],
    });
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      full_name: row.full_name || "",
      email: row.email || "",
      job_title: row.job_title || "",
      department: row.department || "",
      phone: row.phone || "",
      notes: row.notes || "",
      source: row.source || "externo",
      status: row.status || "ativo",
      preferences: row.preferences || EMPTY_FORM.preferences,
      unit_ids: row.unit_ids || [],
    });
    setOpen(true);
  };

  const toggleUnit = (id) => {
    setForm((current) => ({
      ...current,
      unit_ids: current.unit_ids.includes(id)
        ? current.unit_ids.filter((u) => u !== id)
        : [...current.unit_ids, id],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, client_id: clientId };
      if (editing) {
        await api.put(`/envintel/recipients/${editing.id}`, payload);
        toast.success("Destinatário atualizado");
      } else {
        await api.post("/envintel/recipients", payload);
        toast.success("Destinatário cadastrado");
      }
      setOpen(false);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const setRecipientStatus = async (row, next) => {
    try {
      await api.post(`/envintel/recipients/${row.id}/status`, {
        client_id: clientId, status: next,
      });
      toast.success(
        next === "ativo" ? "Destinatário ativado" : "Destinatário desativado",
        {
          description: next === "ativo"
            ? undefined
            : "O histórico de envios anteriores foi preservado.",
        },
      );
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (row) => {
    try {
      await api.delete(`/envintel/recipients/${row.id}`, {
        params: { client_id: clientId },
      });
      toast.success("Destinatário removido do cadastro");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!clientId) {
    return (
      <Card className="p-6 text-sm text-muted-foreground">
        Selecione um cliente para gerenciar os destinatários.
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Buscar por nome ou e-mail"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
          data-testid="recipients-search"
        />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40" data-testid="recipients-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            <SelectItem value="ativo">Ativos</SelectItem>
            <SelectItem value="inativo">Inativos</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex-1" />
        {counts && (
          <span className="text-xs text-muted-foreground">
            {counts.ativos} ativo(s) · {counts.inativos} inativo(s)
          </span>
        )}
        <Button size="sm" onClick={openNew} data-testid="btn-new-recipient">
          <UserPlus size={14} className="mr-1" /> Novo destinatário
        </Button>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b text-left text-xs text-muted-foreground">
            <tr>
              <th className="p-3">Nome</th>
              <th className="p-3">Contato</th>
              <th className="p-3">Unidades</th>
              <th className="p-3">Preferências</th>
              <th className="p-3">Situação</th>
              <th className="p-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="p-6 text-center text-muted-foreground">
                  Nenhum destinatário cadastrado para este cliente.
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={row.id} className="border-b last:border-0">
                <td className="p-3">
                  <div className="font-medium">{row.full_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {row.job_title || "—"}
                    {row.department ? ` · ${row.department}` : ""}
                  </div>
                </td>
                <td className="p-3">
                  <div>{row.email}</div>
                  <div className="text-xs text-muted-foreground">
                    {row.phone || "—"}
                  </div>
                </td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1">
                    {(row.unit_names || []).map((name) => (
                      <Badge key={name} variant="outline">{name}</Badge>
                    ))}
                  </div>
                </td>
                <td className="p-3 text-xs text-muted-foreground">
                  {PREFERENCE_OPTIONS.filter((p) => row.preferences?.[p.key])
                    .map((p) => p.label.replace("Receber ", ""))
                    .join(" · ") || "Nenhuma"}
                </td>
                <td className="p-3">
                  <Badge variant={row.status === "ativo" ? "default" : "secondary"}>
                    {row.status === "ativo" ? "Ativo" : "Inativo"}
                  </Badge>
                  <div className="text-xs text-muted-foreground mt-1">
                    {fmtDateTime(row.registered_at)}
                  </div>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEdit(row)}
                      data-testid={`edit-recipient-${row.id}`}
                    >
                      <Pencil size={14} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setRecipientStatus(
                        row,
                        row.status === "ativo" ? "inativo" : "ativo",
                      )}
                      data-testid={`toggle-recipient-${row.id}`}
                    >
                      {row.status === "ativo" ? "Desativar" : "Ativar"}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600"
                      onClick={() => remove(row)}
                    >
                      Excluir
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Editar destinatário" : "Novo destinatário"}
            </DialogTitle>
            <DialogDescription>
              O contato só recebe comunicações das unidades marcadas e,
              enquanto estiver ativo.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="rc-name">Nome completo *</Label>
              <Input
                id="rc-name"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                data-testid="recipient-name"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="rc-email">E-mail *</Label>
              <Input
                id="rc-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="recipient-email"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="rc-phone">Telefone</Label>
              <Input
                id="rc-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="rc-job">Cargo ou função</Label>
              <Input
                id="rc-job"
                value={form.job_title}
                onChange={(e) => setForm({ ...form, job_title: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="rc-dept">Departamento</Label>
              <Input
                id="rc-dept"
                value={form.department}
                onChange={(e) => setForm({ ...form, department: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Unidades autorizadas *</Label>
            <div className="max-h-40 overflow-y-auto rounded-md border p-2 space-y-1">
              {unitsCatalog.length === 0 && (
                <p className="text-sm text-muted-foreground p-2">
                  Nenhuma unidade autorizada para este cliente.
                </p>
              )}
              {unitsCatalog.map((unit) => (
                <label
                  key={unit.id}
                  className="flex items-center gap-2 text-sm cursor-pointer"
                >
                  <Checkbox
                    checked={form.unit_ids.includes(unit.id)}
                    onCheckedChange={() => toggleUnit(unit.id)}
                    data-testid={`unit-${unit.id}`}
                  />
                  <span>{unit.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Preferências de recebimento</Label>
            {PREFERENCE_OPTIONS.map((pref) => (
              <div
                key={pref.key}
                className="flex items-center justify-between"
              >
                <span className="text-sm">{pref.label}</span>
                <Switch
                  checked={Boolean(form.preferences[pref.key])}
                  onCheckedChange={(checked) => setForm({
                    ...form,
                    preferences: { ...form.preferences, [pref.key]: checked },
                  })}
                  data-testid={`pref-${pref.key}`}
                />
              </div>
            ))}
          </div>


          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>Origem do contato</Label>
              <Select
                value={form.source}
                onValueChange={(v) => setForm({ ...form, source: v })}
              >
                <SelectTrigger data-testid="recipient-source">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="externo">
                    Contato externo (sem login)
                  </SelectItem>
                  <SelectItem value="erp">Usuário do ERP</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Situação</Label>
              <Select
                value={form.status}
                onValueChange={(v) => setForm({ ...form, status: v })}
              >
                <SelectTrigger data-testid="recipient-state">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ativo">Ativo</SelectItem>
                  <SelectItem value="inativo">Inativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {form.source === "externo" && (
            <p className="flex items-start gap-2 rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              <ShieldCheck size={14} className="mt-0.5 shrink-0" />
              Contato externo não recebe conta de acesso ao sistema: o
              cadastro serve somente para as comunicações ambientais.
            </p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              data-testid="recipient-save"
            >
              {saving && <Loader2 className="animate-spin mr-1" size={15} />}
              {editing ? "Salvar alterações" : "Cadastrar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

